import { Fragment, useEffect, useRef, useState } from 'react';
import { ocenaRyzyka as riskApi } from '@/api/endpoints';
import type { RiskPressureDetail } from '@/types';
import { Icon } from '@/components/Icon';

/**
 * Szczegóły obiektu presji wykrytego w obszarze zasilania. Odpowiada na trzy pytania weryfikatora:
 * CO to za obiekt (etykieta + atrybuty źródłowe), JAK leży względem obszaru (odległość/przecięcie)
 * i CO Z NIEGO WYNIKŁO (zagrożenia wyprowadzone dokładnie z tego obiektu).
 *
 * Surowa nazwa ze źródła jest pokazywana obok czytelnej — w dokumencie o skutkach prawnych
 * weryfikator musi móc odtworzyć, z jakiego rekordu w danych państwowych wynika wpis.
 */
export function PressureDetailPanel({ raId, overlayId, onClose, onShowOnMap, onLoaded }: {
  raId: number;
  overlayId: number;
  onClose: () => void;
  onShowOnMap?: (d: RiskPressureDetail) => void;
  /** Zgłasza wczytany obiekt w górę — rodzic podświetla go na mapie już po otwarciu szczegółów. */
  onLoaded?: (d: RiskPressureDetail | null) => void;
}) {
  const [detail, setDetail] = useState<RiskPressureDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // ref, nie prop w deps — inaczej nowa funkcja z rodzica przeładowywałaby szczegóły przy każdym renderze
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    let aktualne = true;
    setDetail(null); setErr(null);
    riskApi.pressureDetail(raId, overlayId)
      .then((d: RiskPressureDetail) => { if (aktualne) { setDetail(d); onLoadedRef.current?.(d); } })
      .catch((e: unknown) => { if (aktualne) setErr(e instanceof Error ? e.message : 'Nie udało się wczytać szczegółów presji.'); });
    return () => { aktualne = false; };
  }, [raId, overlayId]);

  return (
    <div className="rounded border border-ink-100 bg-ink-50/40 min-w-0">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-ink-100 min-w-0">
        <Icon name="pin" className="w-3.5 h-3.5 text-ink-400 shrink-0" />
        <span className="text-[12px] font-semibold text-ink-700 truncate min-w-0">
          {detail?.featureLabel ?? 'Szczegóły presji'}
        </span>
        <div className="ml-auto flex items-center gap-1 shrink-0">
          {detail?.geoJson && onShowOnMap && (
            <button onClick={() => onShowOnMap(detail)} className="btn btn-ghost btn-sm" title="Pokaż obiekt na mapie poglądowej">
              <Icon name="map" className="w-3.5 h-3.5" /> Na mapie
            </button>
          )}
          <button onClick={onClose} className="btn btn-ghost btn-sm" title="Zamknij szczegóły">✕</button>
        </div>
      </div>

      <div className="p-3 grid gap-2.5 text-[12px]">
        {err && <div className="text-red-600">{err}</div>}
        {!detail && !err && <div className="text-ink-400">Wczytywanie…</div>}

        {detail && (
          <>
            <div className="grid grid-cols-[130px_1fr] gap-y-1 min-w-0">
              <span className="text-ink-500">Warstwa</span>
              <span className="text-ink-800 min-w-0 break-words">{detail.layerLabel ?? detail.sourceLayer}</span>
              <span className="text-ink-500">Położenie</span>
              <span className="text-ink-800">
                {detail.intersects
                  ? 'w obszarze zasilania'
                  : detail.distanceM != null ? `${Math.round(detail.distanceM)} m od granicy obszaru` : 'odległość nieustalona'}
              </span>
              {detail.overlapFraction != null && detail.overlapFraction > 0 && (
                <>
                  <span className="text-ink-500">Udział w obszarze</span>
                  <span className="text-ink-800">{(detail.overlapFraction * 100).toFixed(1)} %</span>
                </>
              )}
              <span className="text-ink-500">Zapis źródłowy</span>
              <span className="text-ink-500 font-mono text-[11px] min-w-0 break-all">
                {detail.featureName ?? '—'}{detail.featureId ? ` · ${detail.featureId}` : ''}
              </span>
            </div>

            {detail.attributes.length > 0 && (
              <div className="min-w-0">
                <div className="text-ink-500 mb-1">Atrybuty obiektu ({detail.attributes.length})</div>
                <div className="grid grid-cols-[130px_1fr] gap-y-0.5 min-w-0">
                  {detail.attributes.map(a => (
                    <Fragment key={a.key}>
                      <span className="text-ink-500 truncate min-w-0" title={a.key}>{a.label}</span>
                      <span className="text-ink-800 min-w-0 break-words">{a.value}</span>
                    </Fragment>
                  ))}
                </div>
              </div>
            )}

            <div className="min-w-0">
              <div className="text-ink-500 mb-1">Wyprowadzone zagrożenia ({detail.hazards.length})</div>
              {detail.hazards.length === 0 ? (
                <div className="text-ink-400">
                  Z tego obiektu nie wyprowadzono zagrożenia — regułę reprezentuje inny obiekt tej warstwy
                  albo obiekt nie spełnił warunku odległości.
                </div>
              ) : (
                <div className="grid gap-1">
                  {detail.hazards.map(h => (
                    <div key={h.id} className="flex items-center gap-2 min-w-0">
                      <span className="pill text-[10px] shrink-0" style={{ backgroundColor: h.categoryColor, color: '#fff' }}>
                        {h.categoryName}
                      </span>
                      <span className="text-ink-700 truncate min-w-0" title={h.title}>{h.title}</span>
                      <span className="ml-auto shrink-0 font-mono text-[11px]" style={{ color: h.riskResidualColor }}>
                        {h.riskResidual} · {h.riskResidualLevel}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
